'use client';

import DistanceToNow from '#/components/DistanceToNow';
import EthereumBlockNumber from '#/components/EthereumBlockNumber';
import EthereumHexString from '#/components/EthereumHexString';
import { Badge } from '#/components/ui/badge';
import { Button } from '#/components/ui/button';
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import { Table, TableBody, TableCell, TableRow } from '#/components/ui/table';
import { CopyToClipboard } from '#/components/v2/copy-to-clipboard';
import { useTranslation } from '#/contexts/useTranslation';
import { useConfiguredBlockExplorer } from '#/data/wagmi/configured';
import { TransactionViewOnButton } from '#/modules/display/transaction';
import { useActor } from '@xstate/react';
import { ethers } from 'ethers';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import Link from 'next/link';
import { useContext } from 'react';
import { formatEther } from 'viem';
import {
  useBlock,
  useTransaction,
  useTransactionConfirmations,
  useWaitForTransactionReceipt,
} from 'wagmi';
import { MachineContext } from './contexts/staking-machine';

export function StakingDialogTransaction() {
  const translation = useTranslation(
    translation => translation.EthereumStaking
  );
  const translationApplication = useTranslation(
    translation => translation.Application
  );

  const blockExplorer = useConfiguredBlockExplorer();

  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const { transactionHash } = state.context;

  const { data: transaction, isLoading } = useTransaction({
    hash: transactionHash,
  });
  const { data: confirmations } = useTransactionConfirmations({
    hash: transactionHash,
  });
  const { data: transactionReceipt, isLoading: isTransactionReceiptLoading } =
    useWaitForTransactionReceipt({
      hash: transactionHash,
    });
  const { data: block, isLoading: isBlockLoading } = useBlock({
    blockNumber: transaction?.blockNumber,
    query: {
      enabled: transaction?.blockNumber != null,
    },
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle>{translation.StakingAction.Heading}</DialogTitle>
        <DialogDescription>
          {translation.StakingAction.Transaction.Description}
        </DialogDescription>
      </DialogHeader>
      <Table>
        <TableBody>
          <TableRow className="border-none">
            <TableCell className="font-medium">Transaction Hash</TableCell>
            <TableCell className="">
              <Button
                variant="link"
                size="sm"
                className="px-0 font-mono underline decoration-dashed decoration-gray-300 underline-offset-4"
                asChild
              >
                <Link
                  href={`${blockExplorer?.url}/tx/${transactionHash}`}
                  target="_blank"
                  rel="noreferrer"
                  prefetch={false}
                >
                  <EthereumHexString value={transactionHash} />
                </Link>
              </Button>
              <CopyToClipboard text={transactionHash ?? ''} />
            </TableCell>
          </TableRow>
          <TableRow className="border-none">
            <TableCell className="font-medium">Status</TableCell>
            <TableCell className="">
              {transactionReceipt?.status != null ? (
                transactionReceipt.status === 'reverted' ? (
                  <div className="rounded-md border border-red-300 bg-red-50 text-sm text-red-600 py-0.5 px-2 w-fit">
                    <XCircle className="mr-1 w-3 h-3 inline-block" />
                    {translation.TransactionDialog.FailStatus}
                  </div>
                ) : (
                  <div className="rounded-md border border-green-300 bg-green-50 text-sm text-green-600 py-0.5 px-2 w-fit">
                    <CheckCircle className="mr-1 w-3 h-3 inline-block" />
                    {translation.TransactionDialog.SuccessStatus}
                  </div>
                )
              ) : (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
            </TableCell>
          </TableRow>
          <TableRow className="border-none">
            <TableCell className="font-medium">Block</TableCell>
            <TableCell className="">
              {transaction?.blockNumber != null && confirmations != null ? (
                <div className="flex gap-2 items-center">
                  <EthereumBlockNumber blockNumber={transaction.blockNumber} />
                  <Badge>{confirmations} Block Confirmations</Badge>
                </div>
              ) : (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
            </TableCell>
          </TableRow>
          <TableRow className="border-none">
            <TableCell className="font-medium">Timestamp</TableCell>
            <TableCell className="">
              {block?.timestamp != null ? (
                <DistanceToNow date={Number(block.timestamp) * 1000} />
              ) : (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
            </TableCell>
          </TableRow>
          <TableRow className="border-none">
            <TableCell className="font-medium">Transaction Fee</TableCell>
            <TableCell className="">
              {transaction &&
              transactionReceipt &&
              transaction.gasPrice &&
              transactionReceipt.gasUsed ? (
                `${ethers.utils.commify(
                  formatEther(transaction.gasPrice * transactionReceipt.gasUsed)
                )} ETH`
              ) : (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="secondary">
            {translationApplication.Dialog.CloseButtonLabel}
          </Button>
        </DialogClose>
        <TransactionViewOnButton transactionHash={transactionHash} />
      </DialogFooter>
    </>
  );
}
